// Contracts — work.weern.com photographer cabinet (2026-06-18).
// Worker: apps/work-api/src (weern-work-backend, standalone). Web:
// apps/work-web. Shapes snapped from migrations/0001_init.sql + handlers.
import { z } from 'zod';
import { WorkEvalReasonSchema } from './eval';
import { APP_TARGET_TASKS } from '../platform/participant';

// Categories the photographer can shoot. Extensible — add a literal here and a
// row in the web CATEGORIES array together.
// 'receipts' (2026-08-24): app-web earner gallery uploads — no unique code by
// design, so uploadFrame skips the code gate and the card AI-eval for them.
// 'membership' (2026-08-24): membership/loyalty camera frames get their own
// category so the earner history can pay the membership rate ($1) — the eval
// prompt already covers loyalty cards, and the gift merchant-cap is scoped to
// category='giftcards' so it correctly never applies here.
export const WorkCategorySchema = z.enum(['tickets', 'coupons', 'giftcards', 'receipts', 'membership']);
export type WorkCategory = z.infer<typeof WorkCategorySchema>;

// Contractor capabilities — manager-granted; decide which modules light up in
// the work cabinet. The work account (photographers row) is the contractor
// identity; one account can hold several capabilities (ADR: contractor platform
// port). capture_* = the camera modules already shipped; generator_upload +
// designer arrive with the pothos port.
export const WorkCapabilitySchema = z.enum([
  'capture_printed',
  'capture_giftcards',
  // Загрузка СКРИНШОТОВ (мембершипы / гифт-карты / купоны). Раньше называлось
  // `generator_upload` — переименовано 2026-08-24 по решению владельца:
  // «генератор» — внутреннее слово из pothos, человеку в кабинете оно ничего
  // не говорит. Старое имя оставлено в вокабуляре ХРАНЕНИЯ: оно ещё лежит в
  // строках, заведённых до миграции, и обязано парситься.
  'screenshot',
  /** @deprecated пиши 'screenshot'; принимается только на чтение. */
  'generator_upload',
  'designer',
  // earner = the money-first gift-card cabinet (recruited contractors). Routes the
  // web to the EarnCabinet; on the backend a redeem with role='earner' also grants
  // capture_giftcards + generator_upload so the camera + screenshot uploads work.
  'earner',
  // dev = the Dev department (манифесты и размножение): desktop cabinet where the
  // unit of work is a CELL = locale × типаж (скриншоты/фото билетов и купонов,
  // скриншоты гифт-карт). Routes the web to DevCabinet. No camera, no money UI.
  'dev',
  // sources = «Контроль исходников» (2026-09-11): роль заводит исходники —
  // агрегаторы и единичных мерчантов — с информацией для размножения и следит,
  // где пришли правки. Своё право, НЕ подмножество `dev`: доски заходов,
  // остатков, матрицы и паков у роли нет вовсе.
  'sources',
]);
export type WorkCapability = z.infer<typeof WorkCapabilitySchema>;

/**
 * Право «грузить скриншоты» в обоих написаниях. Пока в базе живут строки с
 * дореименовательным `generator_upload`, КАЖДАЯ проверка этого права обязана
 * принимать оба: воркер и миграция данных выкатываются не атомарно, и между
 * ними шесть живых носителей права остались бы без доступа.
 * Выдавать (INSERT) — всегда только `screenshot`.
 */
export const SCREENSHOT_CAPS = ['screenshot', 'generator_upload'] as const;

/** Каноническое имя права на выдачу. */
export const SCREENSHOT_CAP = 'screenshot' as const;

// Quality band drives the header chip colour. Computed server-side from
// accepted/(accepted+retakes) for the local (UTC v1) day.
export const WorkQualityBandSchema = z.enum(['green', 'lessgreen', 'yellow', 'red']);
export type WorkQualityBand = z.infer<typeof WorkQualityBandSchema>;

// Per-country Informed-Consent document (the canonical pothos registry, resolved
// by the caller's CF-IPCountry). `url` is RELATIVE to the work API — the web
// prepends VITE_WORK_API_BASE (work.weern.com/api/* does NOT route to the worker;
// the workers.dev base does). Surfaced in /me (cabinet link) + /consent.
export const WorkIcfViewSchema = z.object({
  doc_id: z.string(),
  country: z.string(),
  version: z.string(),
  language: z.string(),
  title: z.string(),
  url: z.string(),          // e.g. '/api/work/icf?doc=icf_us_2026-06-02'
});
export type WorkIcfView = z.infer<typeof WorkIcfViewSchema>;

// GET /api/work/me — profile + today's stats. `earnings` is present ONLY when
// the worker's SHOW_EARNINGS flag is '1' (hidden by default per product).
export const WorkMeResSchema = z.object({
  uid: z.string(),
  email: z.string(),
  display_name: z.string(),
  active: z.boolean(),
  today_count: z.number().int().nonnegative(),
  quality_pct: z.number(),            // 0..100; 100 when no frames yet
  quality_band: WorkQualityBandSchema,
  show_earnings: z.boolean(),
  earnings: z
    .object({
      amount: z.number(),
      currency: z.string(),
    })
    .nullable(),
  // Manager-granted capabilities → which modules the cabinet shows. Backfilled
  // capture_printed + capture_giftcards for existing photographers.
  capabilities: z.array(WorkCapabilitySchema),
  // Gift-card merchants that have hit the per-(merchant, country) cap for this
  // photographer's country — the gift home shows them as "done, don't shoot".
  merchant_full: z.array(z.string()).default([]),
  // earner cabinet: has this contractor finished the one-time onboarding flow?
  // false → the cabinet shows onboarding before the EarnCabinet. (migrations/0018)
  onboarded: z.boolean().default(false),
  // Inviter display label (from the `?by=` link, stored at redeem) — the welcome
  // screen shows it even in the PWA. '' → neutral "weern team". (migrations/0021)
  invited_by: z.string().default(''),
  // The per-country ICF resolved for this caller (null when their country has no
  // active doc and no US fallback). The onboarding sign step + the cabinet's
  // "Informed Consent Form" link both read it. (migrations/0019)
  icf: WorkIcfViewSchema.nullable().default(null),
  // Подбор камеры на устройстве (выбор объектива по имени / пробой декодера).
  // Серверный выключатель: если на каком-то парке телефонов подбор сделает
  // хуже, гасим его здесь, без выката фронта. Отсутствует у старого воркера →
  // включено.
  camera_pick: z.boolean().default(true),
  // Сводка ВЕДУЩЕГО фотографа: съёмка идёт через одного человека, остальные
  // работают под ним, поэтому ему показываются цифры ПО ВСЕМ фотографам
  // (сегодня / за всё время) и заработок: ставка в гривнах за каждый кадр +
  // пересчёт в доллары по фикс-курсу. Ролью это НЕ является и нигде не
  // хранится — список email живёт в секрете воркера (WORK_LEAD_EMAILS).
  lead: z.object({
    /** С какой даты считается АРХИВНАЯ часть (съёмка команды — за всё время). */
    since: z.string().default(''),
    /** «Сегодня» — по дню команды (сдвиг WORK_LEAD_TZ_SHIFT), не по UTC. */
    today_all: z.number().int().nonnegative(),
    total_all: z.number().int().nonnegative(),
    rate_uah: z.number(),
    /** Начислено за весь объём — то, что ещё предстоит выплатить. */
    earned_uah: z.number(),
    /** Уже выплачено (конфиг воркера WORK_LEAD_PAID_UAH, обновляется вручную). */
    paid_uah: z.number().default(0),
  }).nullable().default(null),
  // Consent-screen sponsor identity (runtime config; empty strings → the client
  // shows a neutral placeholder). Never committed — set via worker secrets.
  sponsor: z.object({
    name: z.string(),    // short, inline
    legal: z.string(),   // full legal name (big header)
    org: z.string(),     // organizing vendor
  }).default({ name: '', legal: '', org: '' }),
});
export type WorkMeRes = z.infer<typeof WorkMeResSchema>;

// GET /api/work/lead/printQueue — очередь ПЕЧАТИ ведущего фотографа: итоговые
// файлы dev-конвейера (PDF под печать), ждущие печати. Виден только ведущему
// (isLeadEmail); скачивание — /api/work/lead/printFile?card_id= (пишет строку
// журнала dev_activity в том же запросе).
export const WorkLeadPrintQueueResSchema = z.object({
  ok: z.boolean(),
  items: z.array(z.object({
    card_id: z.string(),
    locale: z.string(),
    kind: z.string(),
    /** Агрегатор захода (billetto, sistic…). */
    source: z.string(),
    file_name: z.string(),
    size_bytes: z.number().int().nonnegative(),
    uploaded_at: z.string().default(''),
  })),
});
export type WorkLeadPrintQueueRes = z.infer<typeof WorkLeadPrintQueueResSchema>;

// POST /api/work/uploadFrame — one ACCEPTED frame (failed frames never reach
// the server). Client computed the checks; it reports the metrics + retake
// count so the server can store blur_score and maintain the daily quality
// aggregate. ~9MB base64 ceiling matches the pothos upload path.
export const WorkUploadFrameReqSchema = z.object({
  category: WorkCategorySchema,
  // 'printed' → the frame must match an asset assigned (and pending) to this
  // photographer; the server validates by code and enforces the one-name-per-
  // background guard. 'giftcards' → freeform (global dedup only, as before).
  // Optional + defaulted so the legacy prod client (no mode) keeps working.
  mode: z.enum(['printed', 'giftcards']).default('giftcards'),
  // session_id groups frames from one shooting run; (session_id, bg_batch)
  // is one "batch" (пачка) in the history view. Generated client-side.
  session_id: z.string().trim().min(1).max(64),
  bg_batch: z.number().int().nonnegative(),
  // 1..5 for camera batches; gallery bulk-upload uses the selection index, so
  // the ceiling is generous (one gallery batch = many photos).
  position: z.number().int().min(1).max(1000),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  blur_score: z.number().nonnegative(),
  has_code: z.boolean(),
  code_value: z.string().max(4096).default(''),
  retakes_since_last: z.number().int().nonnegative().default(0),
  // How the frame entered the system: live camera (default, legacy clients send
  // nothing), a picked file, or a file extracted from an uploaded archive. The
  // client-side quality gate is identical for all three.
  via: z.enum(['camera', 'file', 'archive']).default('camera'),
  // Чем именно снят кадр: метка камеры (у телефонов это «camera2 0, facing
  // back» / «Back Ultra Wide Camera»), реальный размер трека, зум и UA. Кадр
  // из camera-пути идёт через canvas, который срезает EXIF, поэтому иначе про
  // устройство и объектив не известно НИЧЕГО — а жалоба «то макро, то
  // широкоугольная» проверяется только этим. Свободная строка: пишем как есть,
  // разбираем запросами.
  capture_meta: z.string().max(400).default(''),
  fileBase64: z.string().trim().min(1).max(9_000_000),
  // 256px JPEG thumbnail for the history grid (~tens of KB base64).
  thumbBase64: z.string().trim().min(1).max(1_500_000),
  mimeType: z.string().trim().min(1).max(100),
});
export type WorkUploadFrameReq = z.infer<typeof WorkUploadFrameReqSchema>;

// Server is authoritative: it re-scans the image for ALL codes and may reject a
// frame the client accepted. Reasons:
//   giftcards (freeform):  duplicate | no_identifier
//   printed (matched):     not_assigned    — code isn't in this photographer's print queue
//                          already_captured — that asset was already shot
//                          name_mismatch    — a different holder name on the same background
// On reject the client treats it like a failed check (don't count, retake). On a
// printed accept, `matched` carries the resolved asset + `remaining` is how many
// of the photographer's assigned items are still pending (cabinet progress).
export const WorkUploadFrameResSchema = z.object({
  status: z.enum(['accepted', 'rejected']),
  id: z.string().optional(),
  reason: z
    .enum(['duplicate', 'no_identifier', 'not_assigned', 'already_captured', 'name_mismatch'])
    .optional(),
  matched: z
    .object({
      holder_name: z.string(),
      event: z.string(),
      category: z.enum(['tickets', 'coupons']),
    })
    .optional(),
  remaining: z.number().int().nonnegative().optional(),
  // name_mismatch: the holder name already locked to this background, so the UI
  // can say "this background is for <name>".
  expected_name: z.string().optional(),
  // giftcards: merchants that have hit the per-(merchant, country) cap for this
  // photographer's country → the app shows a blocking "merchant full" overlay.
  // Lags ~1-2 frames (async LLM merchant detection); over-cap frames are accepted
  // but silently not counted (no reject shown to the photographer).
  merchant_full: z.array(z.string()).optional(),
});
export type WorkUploadFrameRes = z.infer<typeof WorkUploadFrameResSchema>;

// GET /api/work/geo — public; Cloudflare-detected country for auto language.
export const WorkGeoResSchema = z.object({
  country: z.string(),   // ISO-3166 alpha-2, or '' if unknown
});
export type WorkGeoRes = z.infer<typeof WorkGeoResSchema>;

// Display state of one camera frame after the automatic eval gate + any later
// manager/QA review (migrations/0022). Drives the cabinet badges + money:
//   pending  — eval still running (the just-shot last frame); not yet counted
//   credited — auto_passed (or QA-accepted); counts toward earnings
//   review   — needs_qa: held "на проверке", NOT counted until QA accepts
//   rejected — failed the gate (or QA-rejected): never counted
export const WorkCreditStateSchema = z.enum(['pending', 'credited', 'review', 'rejected']);
export type WorkCreditState = z.infer<typeof WorkCreditStateSchema>;

// Reason schema + manifest live in ./eval (ADR-0030); re-used here for the
// per-frame `reason` on history photos + session verdicts.

// GET /api/work/history — the photographer's uploaded batches (пачки), newest
// first. A batch = one (session_id, bg_batch) group. thumb_url is a signed,
// expiring capability URL to /api/work/photo (no Firebase token in the URL).
export const WorkHistoryPhotoSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  has_code: z.boolean(),
  thumb_url: z.string(),
  // Eval/credit state + reason for the per-frame badge (default 'credited' so a
  // legacy worker that omits it keeps the pre-gate behaviour).
  credit_state: WorkCreditStateSchema.default('credited'),
  reason: WorkEvalReasonSchema.default(''),
});
export type WorkHistoryPhoto = z.infer<typeof WorkHistoryPhotoSchema>;

export const WorkHistoryBatchSchema = z.object({
  session_id: z.string(),
  bg_batch: z.number().int(),
  category: WorkCategorySchema,
  count: z.number().int().nonnegative(),
  // Per-state tallies so the cabinet can price earnings off CREDITED frames only
  // and show a separate "на проверке" total. credited+review+rejected+pending=count.
  credited: z.number().int().nonnegative().default(0),
  review: z.number().int().nonnegative().default(0),
  rejected: z.number().int().nonnegative().default(0),
  first_at: z.string(),
  last_at: z.string(),
  photos: z.array(WorkHistoryPhotoSchema),
  // gift cards: distinct detected merchants in this batch (for the history modal).
  merchants: z.array(z.string()).default([]),
});
export type WorkHistoryBatch = z.infer<typeof WorkHistoryBatchSchema>;

// GET /api/work/session/verdicts?session_id=<>[&bg_batch=<>] — per-frame eval
// results for a just-finished camera run, so the reward modal can show each
// asset's outcome. The cabinet polls this (briefly) after the camera closes;
// `resolved` is true once no frame is still pending (eval done for all).
export const WorkSessionVerdictSchema = z.object({
  id: z.string(),
  position: z.number().int(),
  category: WorkCategorySchema,
  state: WorkCreditStateSchema,
  reason: WorkEvalReasonSchema.default(''),
  merchant: z.string().default(''),
});
export type WorkSessionVerdict = z.infer<typeof WorkSessionVerdictSchema>;

export const WorkSessionVerdictsResSchema = z.object({
  resolved: z.boolean(),
  verdicts: z.array(WorkSessionVerdictSchema),
});
export type WorkSessionVerdictsRes = z.infer<typeof WorkSessionVerdictsResSchema>;

export const WorkHistoryResSchema = z.object({
  batches: z.array(WorkHistoryBatchSchema),
});
export type WorkHistoryRes = z.infer<typeof WorkHistoryResSchema>;

// POST /api/work/deletePhoto — фотограф убирает свой кадр из истории (кадр
// снят с лишним предметом в углу / не тем планом). Строка work_photos
// удаляется, байты в R2 остаются, след — в work_photo_deletions.
export const WorkDeletePhotoReqSchema = z.object({
  photo_id: z.string().trim().min(1).max(64),
});
export type WorkDeletePhotoReq = z.infer<typeof WorkDeletePhotoReqSchema>;

// ok=false несёт причину отказа: 'not_found' (не его кадр / уже удалён),
// 'already_exported' (кадр уже выгружен менеджером — удалять поздно).
export const WorkDeletePhotoResSchema = z.object({
  ok: z.boolean(),
  reason: z.enum(['', 'not_found', 'already_exported']).default(''),
});
export type WorkDeletePhotoRes = z.infer<typeof WorkDeletePhotoResSchema>;
// GET /api/work/printLookup?code=<декоднутое значение> — «что это за билет».
// Камера зовёт его, как только live-детект поймал новый код: реестр печати
// знает холдера и событие, которых на самом кадре может не быть, и знает,
// сколько у холдера страниц в пачке. Отсюда счётчик «Kyle · 2 из 5» вместо
// бесполезного «код читается / не читается» (то, что ловит превью, не равно
// тому, что декоднёт сервер).
//
// Коды наружу НЕ отдаём (инвариант print.ts) — только имена и счётчики.
export const WorkPrintLookupQuerySchema = z.object({
  code: z.string().trim().max(4096).default(''),
});
export type WorkPrintLookupQuery = z.infer<typeof WorkPrintLookupQuerySchema>;

export const WorkPrintLookupResSchema = z.object({
  found: z.boolean(),
  holder_name: z.string().default(''),
  event_name: z.string().default(''),
  merchant: z.string().default(''),
  country: z.string().default(''),
  // Сколько страниц у этого холдера в пачке и сколько из них уже снято.
  total: z.number().int().nonnegative().default(0),
  captured: z.number().int().nonnegative().default(0),
  // Этот конкретный билет уже снимали (пересъём/дубль) — камера предупреждает.
  already_captured: z.boolean().default(false),
  // Событие следующей неснятой страницы холдера — РЕКОМЕНДАЦИЯ, что удобно
  // снять дальше. Не обязательство: снимать можно в любом порядке.
  next_event: z.string().default(''),
});
export type WorkPrintLookupRes = z.infer<typeof WorkPrintLookupResSchema>;

// GET /api/work/retakes — кадры, которые надо ПЕРЕСНЯТЬ: код на них не
// прочитался по итогу проверки, значит билет не опознать (ни мерчанта, ни
// страны, ни события) и до отгрузки он не доедет. Фотографу это показывается
// отдельным блоком вместо ленты уведомлений: список конкретных кадров с
// миниатюрами полезнее, чем строка «0 accepted · 4 rejected».
export const WorkRetakeFrameSchema = z.object({
  id: z.string(),
  thumb_url: z.string(),          // подписанная capability-ссылка, как в истории
  created_at: z.string(),
  category: WorkCategorySchema,
  bg_batch: z.number().int().nonnegative().default(0),
  // Почему кадр не опознан: `no_identifier` (уникального кода нет),
  // `no_match` (код есть, но в нашей печати его нет), `decoder_error` (наша
  // авария). Совет фотографу по ним разный — UI выбирает копию по ключу.
  reason: WorkEvalReasonSchema.default(''),
});
export type WorkRetakeFrame = z.infer<typeof WorkRetakeFrameSchema>;

export const WorkRetakesResSchema = z.object({
  frames: z.array(WorkRetakeFrameSchema),
  total: z.number().int().nonnegative().default(0),
});
export type WorkRetakesRes = z.infer<typeof WorkRetakesResSchema>;

// POST /api/work/redeem — self-registration via an invite link. Two forms:
// legacy work.weern.com/?invite=<token> (shared secret), and the short manager
// link work.weern.com/?join=<code> (0025: work_manager_invites row supplies the
// role + inviter label server-side). Either `token` or `join` must be present.
// A signed-in Firebase user redeems → provisioned as a photographer (their
// uid/email bound, active=1). Idempotent.
export const WorkRedeemReqSchema = z.object({
  token: z.string().trim().max(200).default(''),
  // Short manager join code (?join=). Looked up in work_manager_invites; the
  // row's role/label OVERRIDE the body's role/by (the link is the authority).
  join: z.string().trim().max(40).default(''),
  // Which capability tag the invite link grants (additive). photographer =
  // capture_printed ONLY (печатный кабинет, без gift-cards — 2026-07-31);
  // generator = generator_upload; designer = designer (⊇ generator); earner =
  // the money-first gift-card cabinet (⊇ giftcards + generator).
  // Absent → photographer. dev = кабинет Dev-отдела (манифесты и размножение).
  // sources = кабинет «Контроль исходников» (ADR-0095): право `sources` и
  // больше ничего — ни камеры, ни денег, ни Dev-отдела.
  role: z.enum(['photographer', 'generator', 'designer', 'earner', 'dev', 'sources'])
    .default('photographer'),
  // Optional referral code: the contractor whose referral link brought this
  // invitee. Recorded once (first redeem) so the referrer can be credited.
  ref: z.string().trim().max(64).default(''),
  // Optional inviter display label from the `?by=` link (e.g. a manager's name).
  // Stored on the photographer (first-wins) so the onboarding welcome can show it
  // even in the PWA, where the URL param + localStorage are gone. (migrations/0021)
  by: z.string().trim().max(60).default(''),
  // Ключ задачи из ссылки-пресета (`?task=`). С 2026-08-25 он же является
  // ПРИГЛАШЕНИЕМ: ссылки, которые владелец раздаёт из листа участников,
  // состоят только из него, и человек по ним обязан получать доступ
  // (ADR-0068). Это осознанно ОТКРЫТАЯ регистрация — ключей пять и они не
  // секрет; закрывается снятием ветки в `handleRedeem`.
  task: z.enum(APP_TARGET_TASKS).optional(),
});
export type WorkRedeemReq = z.infer<typeof WorkRedeemReqSchema>;

export const WorkRedeemResSchema = z.object({
  ok: z.boolean(),
});
export type WorkRedeemRes = z.infer<typeof WorkRedeemResSchema>;

// POST /api/work/_admin/provision — manager onboards a photographer by email
// + per-photographer rate. uid is bound on the photographer's first sign-in.
export const WorkAdminProvisionReqSchema = z.object({
  email: z.string().trim().email().toLowerCase(),
  display_name: z.string().trim().max(120).default(''),
  rate_amount: z.number().nonnegative().default(0),
  rate_currency: z.string().trim().min(1).max(8).default('USD'),
});
export type WorkAdminProvisionReq = z.infer<typeof WorkAdminProvisionReqSchema>;

export const WorkAdminProvisionResSchema = z.object({
  ok: z.boolean(),
  email: z.string(),
  bound_uid: z.boolean(),   // true if a Firebase uid was already attached
});
export type WorkAdminProvisionRes = z.infer<typeof WorkAdminProvisionResSchema>;
